import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro"
import birthdayService from "../../services/data/birthday.service"
import companyAnniversaryService from "../../services/data/company-anniversary.service"

const MONTH_NAMES = [
    "", "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
]

function BirthdayCard({ emp }) {
    const today = new Date()
    const isToday = emp.birthday_month === today.getMonth() + 1 && emp.birthday_day === today.getDate()
    return (
        <div className={`d-flex align-items-center py-2 border-bottom ${isToday ? "bg-warning bg-opacity-10" : ""}`}>
            {emp.photo
                ? <img src={emp.photo} alt={emp.name} width="36" height="36" className="rounded-circle me-3 object-fit-cover" />
                : <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-3" style={{ width: 36, height: 36, fontSize: 14 }}>
                    {emp.name[0]}
                </div>
            }
            <div className="flex-grow-1">
                <div className="fw-semibold small text-default">
                    {emp.name}
                    {isToday && <span className="ms-2">🎂</span>}
                </div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>{emp.position}</div>
            </div>
            <div className="text-green fw-bold small">
                Dia {emp.birthday_day}
            </div>
        </div>
    )
}

function AnniversaryCard({ emp }) {
    const today = new Date()
    const isToday = emp.hire_month === today.getMonth() + 1 && emp.hire_day === today.getDate()
    return (
        <div className={`d-flex align-items-center py-2 border-bottom ${isToday ? "bg-warning bg-opacity-10" : ""}`}>
            {emp.photo
                ? <img src={emp.photo} alt={emp.name} width="36" height="36" className="rounded-circle me-3 object-fit-cover" />
                : <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-3" style={{ width: 36, height: 36, fontSize: 14 }}>
                    {emp.name[0]}
                </div>
            }
            <div className="flex-grow-1">
                <div className="fw-semibold small text-default">
                    {emp.name}
                    {isToday && <span className="ms-2">🏢</span>}
                </div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>{emp.position}</div>
            </div>
            <div className="text-end">
                <div className="text-green fw-bold small">Dia {emp.hire_day}</div>
                <span className="badge bg-success" style={{ fontSize: "0.65rem" }}>
                    {emp.years_in_company} ano{emp.years_in_company !== 1 ? "s" : ""}
                </span>
            </div>
        </div>
    )
}

function SectionGroup({ icon, label, employees, CardComponent }) {
    if (!employees?.length) return null
    return (
        <div className="mb-4">
            <div className="fw-bold text-default mb-2">
                <FontAwesomeIcon icon={icon} className="me-2 text-green" />
                {label}
            </div>
            {employees.map((emp, i) => <CardComponent key={i} emp={emp} />)}
        </div>
    )
}

export default function BirthdayCalendar() {
    const companyId = localStorage.getItem("companyId")
    const today = new Date()
    const [month, setMonth] = useState(today.getMonth() + 1)
    const [year, setYear] = useState(today.getFullYear())
    const [data, setData] = useState(null)
    const [annivData, setAnnivData] = useState(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        loadCalendar()
    }, [month, year])

    function loadCalendar() {
        setLoading(true)
        const payload = { company_id: companyId, month, year }
        Promise.all([
            birthdayService.getCalendar(payload),
            companyAnniversaryService.getCalendar(payload).catch(() => null),
        ])
            .then(([bd, an]) => { setData(bd); setAnnivData(an) })
            .catch(console.error)
            .finally(() => setLoading(false))
    }

    function prevMonth() {
        if (month === 1) { setMonth(12); setYear(y => y - 1) }
        else setMonth(m => m - 1)
    }

    function nextMonth() {
        if (month === 12) { setMonth(1); setYear(y => y + 1) }
        else setMonth(m => m + 1)
    }

    function handleExportIcs() {
        window.open(birthdayService.exportIcsUrl(companyId), "_blank")
    }

    function asArray(value) {
        return Array.isArray(value) ? value : []
    }

    const birthdayGroups = data
        ? {
            myself: asArray(data.myself),
            subordinates: asArray(data.subordinates),
            peers: asArray(data.peers),
            superior: asArray(data.superior),
        }
        : null

    const bdTotal = birthdayGroups
        ? birthdayGroups.myself.length + birthdayGroups.subordinates.length + birthdayGroups.peers.length + birthdayGroups.superior.length
        : 0

    const anniversaryGroups = annivData
        ? {
            myself: asArray(annivData.myself),
            subordinates: asArray(annivData.subordinates),
            peers: asArray(annivData.peers),
            superior: asArray(annivData.superior),
        }
        : null

    const anTotal = anniversaryGroups
        ? anniversaryGroups.myself.length + anniversaryGroups.subordinates.length + anniversaryGroups.peers.length + anniversaryGroups.superior.length
        : 0

    const hasAnniversaries = anTotal > 0

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center gap-2">
                    <span className="page-link text-default cursorPointer" onClick={prevMonth}>
                        <FontAwesomeIcon className='' icon={solid('angle-left')} />
                    </span>
                    <span className="fw-bold text-default" style={{ minWidth: 160, textAlign: "center" }}>
                        {MONTH_NAMES[month]} {year}
                    </span>
                    <span className="page-link text-default cursorPointer" onClick={nextMonth}>
                        <FontAwesomeIcon className='' icon={solid('angle-right')} />
                    </span>
                </div>
                <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-success px-3 rounded-pill" onClick={handleExportIcs} title="Exportar para Google Calendar / Outlook">
                        Exportar Calendário
                    </button>
                </div>
            </div>

            {loading && <div className="text-center text-muted py-4">Carregando...</div>}

            {!loading && data && (
                <>
                    <SectionGroup icon={solid("user")} label="Você" employees={birthdayGroups?.myself} CardComponent={BirthdayCard} />
                    <SectionGroup icon={solid("users")} label="Subordinados Diretos" employees={birthdayGroups?.subordinates} CardComponent={BirthdayCard} />
                    <SectionGroup icon={solid("handshake")} label="Pares" employees={birthdayGroups?.peers} CardComponent={BirthdayCard} />
                    <SectionGroup icon={solid("arrow-up")} label="Gestor Imediato" employees={birthdayGroups?.superior} CardComponent={BirthdayCard} />

                    {bdTotal === 0 && !hasAnniversaries && (
                        <div className="text-center text-muted py-4">
                            <FontAwesomeIcon icon={solid("calendar")} size="2x" className="mb-2" />
                            <div>Nenhum aniversário em {MONTH_NAMES[month]}</div>
                        </div>
                    )}

                    {hasAnniversaries && (
                        <>
                            <div className="d-flex align-items-center gap-2">
                                <span className="badge bg-success rounded-pill d-inline-flex align-items-center p-2">{bdTotal} aniversariante{bdTotal !== 1 ? "s" : ""}</span>
                                {hasAnniversaries && (
                                    <span className="badge bg-success rounded-pill d-inline-flex align-items-center p-2">{anTotal} aniversariante{anTotal !== 1 ? "s" : ""} de empresa</span>
                                )}
                            </div>
                           
                            <hr className="my-4" />
                            <div className="fw-bold text-default mb-3">
                                <FontAwesomeIcon icon={solid("building")} className="me-2 text-green" />
                                Aniversários de Empresa
                                {annivData.frequency === "every_5_years" && (
                                    <span className="badge bg-secondary ms-2" style={{ fontSize: "0.7rem" }}>A cada 5 anos</span>
                                )}
                            </div>
                            <SectionGroup icon={solid("user")} label="Você" employees={anniversaryGroups?.myself} CardComponent={AnniversaryCard} />
                            <SectionGroup icon={solid("users")} label="Subordinados Diretos" employees={anniversaryGroups?.subordinates} CardComponent={AnniversaryCard} />
                            <SectionGroup icon={solid("handshake")} label="Pares" employees={anniversaryGroups?.peers} CardComponent={AnniversaryCard} />
                            <SectionGroup icon={solid("arrow-up")} label="Gestor Imediato" employees={anniversaryGroups?.superior} CardComponent={AnniversaryCard} />
                        </>
                    )}
                </>
            )}
        </div>
    )
}
